<script setup>
// SiteUP CRM (Módulo Imobiliária): a folha da ficha do lead, a mesma dentro do
// modal e na página de impressão. É papel: fundo branco e tinta escura nos dois
// temas, porque é o que sai na impressora. Travada por padrão; com `editable`
// os campos de texto viram inputs (F5). Os imóveis nunca são editáveis: vêm do
// inventário.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  LEAD_SHEET_SECTIONS,
  LEAD_SHEET_SERVICE_SECTION,
  formatGeneratedAt,
} from './leadSheet';

const props = defineProps({
  sheet: { type: Object, required: true },
  editable: { type: Boolean, default: false },
  print: { type: Boolean, default: false },
});

const emit = defineEmits(['updateField']);

const { t, locale } = useI18n();

const label = key => t(`CONVERSATION.LEAD_SHEET.${key}`);
const fieldLabel = key =>
  t('CONVERSATION.LEAD_SHEET.FIELD_LABEL', { label: label(`FIELDS.${key}`) });

const footer = computed(() => {
  const { date, time } = formatGeneratedAt(
    props.sheet.generatedAt,
    locale.value
  );
  const params = {
    date,
    time,
    name: props.sheet.generatedBy,
    id: props.sheet.conversationId,
  };
  return props.sheet.generatedBy
    ? t('CONVERSATION.LEAD_SHEET.FOOTER_BY', params)
    : t('CONVERSATION.LEAD_SHEET.FOOTER', params);
});

const update = (section, key, event) => {
  emit('updateField', { section, key, value: event.target.value });
};
</script>

<template>
  <article
    class="lead-sheet-paper flex flex-col gap-3"
    :class="{ 'lead-sheet-paper--print': print }"
    data-testid="lead-sheet-document"
  >
    <header class="lead-sheet-head">
      <h3>{{ label('SHEET_TITLE') }}</h3>
      <div class="lead-sheet-org">
        {{ sheet.org.nome }}
        <span v-if="sheet.org.detalhe" class="block font-normal">
          {{ sheet.org.detalhe }}
        </span>
      </div>
    </header>

    <section
      v-for="section in LEAD_SHEET_SECTIONS"
      :key="section.key"
      class="lead-sheet-section"
    >
      <h4>{{ label(`SECTIONS.${section.label}`) }}</h4>
      <div class="lead-sheet-grid">
        <component
          :is="editable ? 'label' : 'div'"
          v-for="field in section.fields"
          :key="field.key"
          class="lead-sheet-field"
          :class="{ 'lead-sheet-field--full': field.full }"
        >
          <span class="lead-sheet-key">
            {{ fieldLabel(field.label) }}
          </span>
          <input
            v-if="editable"
            class="lead-sheet-input"
            :value="sheet[section.key][field.key]"
            :placeholder="label('EMPTY')"
            :data-testid="`lead-sheet-input-${section.key}-${field.key}`"
            @input="update(section.key, field.key, $event)"
          />
          <span
            v-else
            class="lead-sheet-value"
            :class="{
              'lead-sheet-value--empty': !sheet[section.key][field.key],
            }"
            :data-testid="`lead-sheet-value-${section.key}-${field.key}`"
          >
            {{ sheet[section.key][field.key] || label('EMPTY') }}
          </span>
        </component>
      </div>
    </section>

    <section class="lead-sheet-section">
      <h4>{{ label('SECTIONS.PROPERTIES') }}</h4>
      <div v-if="sheet.imoveis.length" class="overflow-x-auto">
        <table class="lead-sheet-table">
          <thead>
            <tr>
              <th>{{ label('TABLE.CODE') }}</th>
              <th>{{ label('TABLE.NEIGHBORHOOD') }}</th>
              <th class="text-end">{{ label('TABLE.VALUE') }}</th>
              <th class="text-end">{{ label('TABLE.BEDROOMS') }}</th>
              <th class="text-end">{{ label('TABLE.PARKING') }}</th>
              <th class="text-end">{{ label('TABLE.AREA') }}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in sheet.imoveis" :key="row.codigo">
              <td class="font-bold">{{ row.codigo }}</td>
              <td>{{ row.bairro }}</td>
              <td class="text-end">{{ row.valor }}</td>
              <td class="text-end">{{ row.dormitorios }}</td>
              <td class="text-end">{{ row.vagas }}</td>
              <td class="text-end">{{ row.area }}</td>
              <td>
                <span
                  v-if="row.status"
                  class="lead-sheet-status"
                  :class="{
                    'lead-sheet-status--ref': row.status === 'visit_scheduled',
                  }"
                >
                  {{ label(`STATUS.${row.status.toUpperCase()}`) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="lead-sheet-value--empty">
        {{ label('NO_PROPERTIES') }}
      </p>
    </section>

    <section class="lead-sheet-section">
      <h4>{{ label(`SECTIONS.${LEAD_SHEET_SERVICE_SECTION.label}`) }}</h4>
      <div class="lead-sheet-grid">
        <component
          :is="editable ? 'label' : 'div'"
          v-for="field in LEAD_SHEET_SERVICE_SECTION.fields"
          :key="field.key"
          class="lead-sheet-field"
        >
          <span class="lead-sheet-key">
            {{ fieldLabel(field.label) }}
          </span>
          <input
            v-if="editable"
            class="lead-sheet-input"
            :value="sheet.atendimento[field.key]"
            :placeholder="label('EMPTY')"
            :data-testid="`lead-sheet-input-atendimento-${field.key}`"
            @input="update('atendimento', field.key, $event)"
          />
          <span
            v-else
            class="lead-sheet-value"
            :class="{
              'lead-sheet-value--empty': !sheet.atendimento[field.key],
            }"
          >
            {{ sheet.atendimento[field.key] || label('EMPTY') }}
          </span>
        </component>
      </div>
    </section>

    <section class="lead-sheet-section">
      <h4>{{ label('SECTIONS.NOTES') }}</h4>
      <textarea
        v-if="editable"
        class="lead-sheet-notes lead-sheet-input"
        rows="3"
        :value="sheet.observacoes"
        :aria-label="label('SECTIONS.NOTES')"
        data-testid="lead-sheet-input-observacoes"
        @input="update(null, 'observacoes', $event)"
      />
      <p v-else class="lead-sheet-notes whitespace-pre-line">
        {{ sheet.observacoes }}
      </p>
    </section>

    <footer class="lead-sheet-foot">{{ footer }}</footer>
  </article>
</template>

<style>
/* Papel: cores fixas de propósito, iguais no tema claro e no escuro. */
.lead-sheet-paper {
  background: #fff;
  color: #14181c;
  border: 1px solid #d5dbe0;
  border-radius: 8px;
  padding: 22px 24px;
  font-size: 13px;
  line-height: 1.35;
}
.lead-sheet-paper--print {
  border: 0;
  border-radius: 0;
  padding: 40px 44px;
  min-height: 1000px;
  font-size: 13.5px;
}
.lead-sheet-head {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  border-bottom: 2px solid #14181c;
  padding-bottom: 8px;
}
.lead-sheet-head h3 {
  flex: 1;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.02em;
  margin: 0;
  color: #14181c;
}
.lead-sheet-org {
  font-size: 12px;
  font-weight: 700;
  text-align: end;
  color: #5b6670;
}
.lead-sheet-section h4 {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5b6670;
  margin: 0 0 6px;
  border-bottom: 1px solid #d5dbe0;
  padding-bottom: 3px;
}
.lead-sheet-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 18px;
}
.lead-sheet-field {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  margin: 0;
  font-size: inherit;
  color: inherit;
}
.lead-sheet-field--full {
  grid-column: 1 / -1;
}
.lead-sheet-key {
  color: #5b6670;
  flex: none;
}
.lead-sheet-value {
  font-weight: 700;
  min-width: 0;
  overflow-wrap: anywhere;
}
.lead-sheet-value--empty {
  font-weight: 400;
  color: #9aa4ad;
}
.lead-sheet-input {
  flex: 1;
  min-width: 0;
  font: inherit;
  font-weight: 700;
  color: #14181c;
  background: transparent;
  border: 0;
  border-radius: 4px;
  padding: 0 3px;
  margin: 0 -3px;
  height: auto;
  outline: 1px dashed #d5dbe0;
  box-shadow: none;
}
.lead-sheet-input:hover,
.lead-sheet-input:focus {
  background: rgba(29, 155, 240, 0.06);
  outline: 1px solid #1d9bf0;
}
.lead-sheet-input::placeholder {
  font-weight: 400;
  color: #9aa4ad;
}
.lead-sheet-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}
.lead-sheet-table th {
  font-size: 11px;
  font-weight: 700;
  text-align: start;
  color: #5b6670;
  padding: 4px 6px;
  border-bottom: 1px solid #d5dbe0;
}
.lead-sheet-table th.text-end,
.lead-sheet-table td.text-end {
  text-align: end;
}
.lead-sheet-table td {
  padding: 5px 6px;
  border-bottom: 1px solid #eef1f3;
  font-variant-numeric: tabular-nums;
  color: #14181c;
}
.lead-sheet-status {
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 999px;
  border: 1px solid #d5dbe0;
  white-space: nowrap;
}
.lead-sheet-status--ref {
  border-color: #1d9bf0;
  color: #0b6fb0;
}
.lead-sheet-notes {
  min-height: 36px;
  width: 100%;
  border: 1px solid #d5dbe0;
  border-radius: 6px;
  padding: 6px 8px;
  margin: 0;
  font-weight: 400;
  resize: vertical;
}
textarea.lead-sheet-notes {
  outline: 0;
}
.lead-sheet-foot {
  font-size: 10.5px;
  color: #5b6670;
  border-top: 1px solid #d5dbe0;
  padding-top: 6px;
}
@media (max-width: 640px) {
  .lead-sheet-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .lead-sheet-paper {
    padding: 16px;
  }
  .lead-sheet-paper--print {
    padding: 22px 18px;
    min-height: 0;
  }
}
</style>
